import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiPlay, HiPause, HiVolumeUp, HiVolumeOff, HiHeart, HiOutlineHeart, HiDotsVertical, HiDownload, HiInformationCircle } from 'react-icons/hi';
import { FaSpotify, FaYoutube } from 'react-icons/fa';
import { IoIosSkipBackward, IoIosSkipForward } from 'react-icons/io';
import { MdRepeat, MdRepeatOne } from 'react-icons/md';
import { supabase } from '../supabaseClient';
import Navigation from './Navigation';

// Function to extract dominant color from image
const extractDominantColor = (imageUrl) => {
  // Return a default dark color for videos/GIFs
  if (imageUrl && (imageUrl.endsWith('.mp4') || imageUrl.endsWith('.gif'))) {
    return Promise.resolve('rgb(20, 20, 20)');
  }
  
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = imageUrl;
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = Math.min(img.width, 200); // Reduce size for performance
      canvas.height = Math.min(img.height, 200);
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      let r = 0, g = 0, b = 0;
      let count = 0;
      
      // Sample pixels to find dominant color
      for (let i = 0; i < data.length; i += 40) {
        // Skip transparent pixels
        if (data[i + 3] < 125) continue;
        
        r += data[i];
        g += data[i + 1];
        b += data[i + 2];
        count++;
      }
      
      if (count > 0) {
        r = Math.floor(r / count);
        g = Math.floor(g / count);
        b = Math.floor(b / count);
      } else {
        // If no suitable pixels found, use a neutral color
        r = 30;
        g = 30;
        b = 30;
      }
      
      // Adjust color for better background contrast
      const brightness = (r + g + b) / 3;
      
      // For very bright colors, we'll create a subtle gradient
      // For darker colors, we'll darken them appropriately
      if (brightness > 180) {
        // Light color - slightly darken it for background use
        r = Math.floor(r * 0.8);
        g = Math.floor(g * 0.8);
        b = Math.floor(b * 0.8);
      } else if (brightness > 100) {
        // Medium brightness - moderate darkening
        r = Math.floor(r * 0.6);
        g = Math.floor(g * 0.6);
        b = Math.floor(b * 0.6);
      } else {
        // Dark color - significant darkening
        r = Math.floor(r * 0.4);
        g = Math.floor(g * 0.4);
        b = Math.floor(b * 0.4);
      }
      
      resolve(`rgb(${r}, ${g}, ${b})`);
    };
    
    img.onerror = () => {
      // Fallback to default dark color (deep gray)
      resolve('rgb(20, 20, 20)');
    };
  });
};

const SingleTrack = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [likedTracks, setLikedTracks] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showCredits, setShowCredits] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('rgb(20, 20, 20)');
  const [repeatMode, setRepeatMode] = useState('off'); // 'off', 'one'
  const audioRef = useRef(null);
  const menuRef = useRef(null);
  const hasLoadedLikedTracks = useRef(false);
  const progressInterval = useRef(null);

  // Fetch track from Supabase
  useEffect(() => {
    const fetchTrack = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('music_tracks')
          .select('*')
          .eq('id', trackId)
          .single();
        
        if (error) {
          console.error('Error fetching track:', error);
          navigate('/music'); // Redirect to music page if track not found
        } else if (data) {
          setTrack(data);
          
          // Extract dominant color from album art
          if (data.cover_url) {
            const color = await extractDominantColor(data.cover_url);
            setBackgroundColor(color);
          }
        } else {
          navigate('/music'); // Redirect to music page if track not found
        }
      } catch (err) {
        console.error('Error:', err);
        navigate('/music');
      } finally {
        setLoading(false);
      }
    };
    
    if (trackId) {
      fetchTrack();
    }
  }, [trackId, navigate]);

  // Function to update meta tags for social sharing
  const updateMetaTags = (trackData) => {
    // Update title
    const title = `${trackData.title} by ${trackData.artist || 'Unknown Artist'}`;
    document.title = title;
    
    // For social sharing, we'll use a fallback image for videos/GIFs
    const coverImage = trackData.cover_url && !(trackData.cover_url.endsWith('.mp4') || trackData.cover_url.endsWith('.gif')) 
      ? trackData.cover_url 
      : `${window.location.origin}/og-image.png`;
    
    // Update or create meta tags
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', `Listen to "${trackData.title}" by ${trackData.artist || 'Unknown Artist'} on Kryptik's music collection`);
    updateMetaTag('og:image', coverImage);
    updateMetaTag('og:url', `${window.location.origin}/music/${trackData.id}`);
    
    // Twitter cards
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', `Listen to "${trackData.title}" by ${trackData.artist || 'Unknown Artist'} on Kryptik's music collection`);
    updateMetaTag('twitter:image', coverImage);
  };

  // Helper function to update or create meta tags
  const updateMetaTag = (property, content) => {
    let metaTag = document.querySelector(`meta[property="${property}"]`) || 
                  document.querySelector(`meta[name="${property}"]`);
    
    if (metaTag) {
      metaTag.setAttribute('content', content);
    } else {
      metaTag = document.createElement('meta');
      metaTag.setAttribute('property', property);
      metaTag.setAttribute('content', content);
      document.getElementsByTagName('head')[0].appendChild(metaTag);
    }
  };

  // Load liked tracks from localStorage on component mount
  useEffect(() => {
    if (!hasLoadedLikedTracks.current) {
      const savedLikedTracks = localStorage.getItem('likedTracks');
      if (savedLikedTracks) {
        try {
          const parsedLikedTracks = JSON.parse(savedLikedTracks);
          if (Array.isArray(parsedLikedTracks)) {
            setLikedTracks(new Set(parsedLikedTracks));
          }
        } catch (error) {
          console.error('Error parsing liked tracks from localStorage:', error);
          localStorage.removeItem('likedTracks'); // Clear invalid data
        }
      }
      hasLoadedLikedTracks.current = true;
    }
  }, []);

  // Save liked tracks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('likedTracks', JSON.stringify([...likedTracks]));
  }, [likedTracks]);

  // Handle audio playback
  useEffect(() => {
    if (audioRef.current && track) {
      if (isPlaying) {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, track]);

  // Sync video playback with audio
  useEffect(() => {
    // Get all video elements in the component
    const videoElements = document.querySelectorAll('video');
    
    if (isPlaying) {
      // Play all videos when audio plays
      videoElements.forEach(video => {
        if (video) {
          video.play().catch(error => {
            console.log('Error playing video:', error);
          });
        }
      });
    } else {
      // Pause all videos when audio pauses
      videoElements.forEach(video => {
        if (video) {
          video.pause();
        }
      });
    }
  }, [isPlaying]);

  // Update progress bar using interval for more reliable updates
  useEffect(() => {
    // Clear any existing interval
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    
    if (isPlaying && audioRef.current) {
      // Set up interval to update progress
      progressInterval.current = setInterval(() => {
        const audio = audioRef.current;
        if (audio) {
          const current = audio.currentTime;
          const dur = audio.duration || 1;
          setCurrentTime(current);
          setDuration(dur);
          setProgress((current / dur) * 100);
        }
      }, 100); // Update every 100ms for smooth progress
    }
    
    // Cleanup function
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, track, repeatMode]); // Added repeatMode to dependencies

  // Handle track end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleEnded = () => {
      if (repeatMode === 'one') {
        // Repeat current track
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(error => {
            console.error('Error replaying audio:', error);
            setIsPlaying(false);
          });
        }
      } else {
        // Default behavior: restart and pause
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
          setProgress(0);
          setCurrentTime(0);
        }
      }
    };
    
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
      // Clear interval on unmount
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [track, repeatMode]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const toggleLike = async (trackId) => {
    const newLikedTracks = new Set(likedTracks);
    const isCurrentlyLiked = newLikedTracks.has(trackId);
    
    if (isCurrentlyLiked) {
      newLikedTracks.delete(trackId);
    } else {
      newLikedTracks.add(trackId);
    }
    
    setLikedTracks(newLikedTracks);
    
    // Update the likes count in the database
    try {
      const newLikes = isCurrentlyLiked ? Math.max(0, (track.likes || 0) - 1) : (track.likes || 0) + 1;
      
      // Update the database
      const { error } = await supabase
        .from('music_tracks')
        .update({ likes: newLikes })
        .eq('id', trackId);
      
      if (error) {
        console.error('Error updating likes:', error);
      } else {
        // Update local state
        setTrack(prev => ({ ...prev, likes: newLikes }));
      }
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const handleProgressChange = (e) => {
    const newProgress = parseFloat(e.target.value);
    setProgress(newProgress);
    if (audioRef.current && track) {
      const duration = audioRef.current.duration || 1;
      audioRef.current.currentTime = (newProgress / 100) * duration;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const openCredits = () => {
    setShowCredits(true);
  };

  const closeCredits = () => {
    setShowCredits(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col">
        {/* Blurred background album art */}
        {track && track.cover_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: !(track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? `url(${track.cover_url})` : 'none',
              backgroundColor: (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? '#000' : 'transparent',
              filter: 'blur(80px)',
              transform: 'scale(1.2)'
            }}
          >
            {(track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) && (
              <video 
                src={track.cover_url} 
                className="absolute inset-0 w-full h-full object-cover opacity-30"
                ref={(video) => {
                  if (video) {
                    // Sync video with audio playback
                    if (isPlaying) {
                      video.play().catch(e => console.log("Video play error:", e));
                    } else {
                      video.pause();
                    }
                  }
                }}
                loop
                muted
                playsInline
              />
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-black/70"></div>
        
        <div className="flex-grow flex items-center justify-center relative z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
            <p>Loading track...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!track) {
    return (
      <div className="fixed inset-0 flex flex-col">
        <div className="flex-grow flex items-center justify-center px-4 relative z-10">
          <div className="text-center">
            <h1 className="text-display-2 font-space-grotesk font-bold mb-4">
              <span className="gradient-text-green">Track Not Found</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-xs sm:max-w-md mx-auto mb-8">
              The track you're looking for doesn't exist or has been removed.
            </p>
            <button 
              onClick={() => navigate('/music')}
              className="btn-primary px-6 py-3 text-sm sm:text-base"
            >
              Back to Music
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: `linear-gradient(135deg, ${backgroundColor}, rgb(10, 10, 10))` }}>
      {/* Navigation */}
      <Navigation />
      
      {/* Blurred background album art */}
      <div 
        className="absolute inset-0 bg-cover bg-center sm:pt-0 pt-20" // Only add padding on mobile (sm:pt-0 means no padding on small screens and up)
        style={{ 
          backgroundImage: track.cover_url && !(track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? `url(${track.cover_url})` : 'none',
          backgroundColor: track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? '#000' : 'transparent',
          filter: 'blur(80px)',
          transform: 'scale(1.2)'
        }}
      >
        {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) && (
          <video 
            src={track.cover_url} 
            className="absolute inset-0 w-full h-full object-cover"
            ref={(video) => {
              if (video) {
                // Sync video with audio playback
                if (isPlaying) {
                  video.play().catch(e => console.log("Video play error:", e));
                } else {
                  video.pause();
                }
              }
            }}
            loop
            muted
            playsInline
          />
        )}
      </div>
      {/* Dark overlay for better readability - reduced opacity for video backgrounds */}
      <div 
        className="absolute inset-0 sm:pt-0 pt-20"
        style={{
          backgroundColor: track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.7)'
        }}
      ></div>
      
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={track.file_url}
        volume={volume}
      />
      
      {/* Main content area with top padding only on mobile to avoid navbar overlap */}
      <div className="flex-grow flex flex-col justify-center px-6 sm:px-8 overflow-hidden relative z-10 sm:pt-0 pt-16">
        {/* Main Player Section - Fully responsive layout */}
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col justify-center">
          {/* Mobile Layout - Album art with info below and left-aligned */}
          <div className="lg:hidden flex flex-col items-center w-full">
            {/* Album Art */}
            <div className="flex justify-center items-center mb-6 w-full max-w-md">
              <motion.div 
                className="relative w-full aspect-square rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                  <video 
                    src={track.cover_url} 
                    alt={track.title}
                    className="w-full h-full object-cover"
                    ref={(video) => {
                      if (video) {
                        // Sync video with audio playback
                        if (isPlaying) {
                          video.play().catch(e => console.log("Video play error:", e));
                        } else {
                          video.pause();
                        }
                      }
                    }}
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img 
                    src={track.cover_url} 
                    alt={track.title}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                {/* Vinyl-like effect */}
                <div className="absolute inset-0 border-2 border-white/5 rounded-full opacity-20"></div>
                {/* Overlay Track Info on Album Art for Mobile */}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h2 className="text-xl font-space-grotesk font-bold mb-1 leading-tight text-white drop-shadow-md">
                    {track.title}
                  </h2>
                  <p className="text-base text-gray-200 mb-1 drop-shadow-md">
                    {track.artist || 'Unknown Artist'}
                  </p>
                  <p className="text-sm text-gray-300 drop-shadow-md">{track.album || 'Single'}</p>
                </div>
              </motion.div>
            </div>
            
            {/* Track Info - Hidden on mobile since it's now overlaid on album art */}
            <div className="w-full max-w-xs sm:max-w-sm mb-8 text-left hidden">
              <h2 className="text-xl sm:text-2xl font-space-grotesk font-bold mb-1 leading-tight">
                {track.title}
              </h2>
              <p className="text-base sm:text-lg text-gray-300 mb-1">
                {track.artist || 'Unknown Artist'}
              </p>
              <p className="text-sm sm:text-base text-gray-400 mb-3">{track.album || 'Single'}</p>
            </div>
          </div>
          
          {/* Desktop Layout - Two column grid */}
          <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 items-center">
            {/* Album Art */}
            <div className="flex justify-center items-center h-full max-h-[45vh] lg:max-h-none">
              <motion.div 
                className="relative w-56 sm:w-56 md:w-64 md:h-64 lg:w-72 lg:h-72 xl:w-80 xl:h-80 2xl:w-96 2xl:h-96 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                  <video 
                    src={track.cover_url} 
                    alt={track.title}
                    className="w-full h-full object-cover"
                    ref={(video) => {
                      if (video) {
                        // Sync video with audio playback
                        if (isPlaying) {
                          video.play().catch(e => console.log("Video play error:", e));
                        } else {
                          video.pause();
                        }
                      }
                    }}
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <img 
                    src={track.cover_url} 
                    alt={track.title}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                {/* Vinyl-like effect */}
                <div className="absolute inset-0 border-2 sm:border-4 border-white/5 rounded-full opacity-20"></div>
              </motion.div>
            </div>

            {/* Track Info & Controls - Responsive layout with proper spacing */}
            <div className="flex flex-col justify-center space-y-4 sm:space-y-5 md:space-y-6 h-full max-h-[50vh] lg:max-h-none">
              {/* Track Information - Left aligned on desktop */}
              <div className="text-left">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-space-grotesk font-bold mb-1 sm:mb-2 leading-tight">
                  {track.title}
                </h2>
                <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-1">
                  {track.artist || 'Unknown Artist'}
                </p>
                <p className="text-base md:text-lg text-gray-400">{track.album || 'Single'}</p>
              </div>
              
              {/* Progress Bar and Controls - Desktop layout */}
              <div className="space-y-3 sm:space-y-4">
                {/* Progress Bar */}
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex justify-between text-xs sm:text-sm text-gray-400">
                    <span>{formatTime(audioRef.current ? audioRef.current.currentTime : 0)}</span>
                    <span>{formatTime(audioRef.current ? audioRef.current.duration : 0)}</span>
                  </div>
                  <div className="relative h-1 sm:h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-accent-green to-cyan-400 rounded-full"
                      style={{ width: `${progress}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={handleProgressChange}
                      className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Controls Row - Play button on right on desktop */}
                <div className="flex items-center justify-between">
                  {/* Additional Controls - Left side */}
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    {/* Volume Control */}
                    <div className="flex items-center space-x-2 w-20 sm:w-24 md:w-28">
                      <button 
                        onClick={toggleMute}
                        className="text-gray-300 hover:text-white transition-colors"
                      >
                        {isMuted || volume === 0 ? (
                          <HiVolumeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                        ) : (
                          <HiVolumeUp className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </button>
                      <div className="relative h-1 bg-gray-700 rounded-full overflow-hidden flex-1">
                        <div 
                          className="absolute top-0 left-0 h-full bg-accent-green rounded-full"
                          style={{ width: `${isMuted ? 0 : volume * 100}%` }}
                        ></div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={isMuted ? 0 : volume}
                          onChange={handleVolumeChange}
                          className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <button 
                          onClick={() => toggleLike(track.id)}
                          className={`p-1.5 rounded-full ${
                            likedTracks.has(track.id) 
                              ? 'text-red-500 bg-red-500/20' 
                              : 'text-gray-300 hover:text-red-500 hover:bg-red-500/20'
                          } transition-colors`}
                        >
                          {likedTracks.has(track.id) ? (
                            <HiHeart className="h-5 w-5" />
                          ) : (
                            <HiOutlineHeart className="h-5 w-5" />
                          )}
                        </button>
                        {track.likes > 0 && (
                          <span className="text-gray-400 text-sm ml-1">{track.likes}</span>
                        )}
                      </div>
                      
                      {/* Repeat Button */}
                      <button
                        onClick={() => {
                          if (repeatMode === 'off') {
                            setRepeatMode('one');
                          } else {
                            setRepeatMode('off');
                          }
                        }}
                        className={`p-1.5 rounded-full ${
                          repeatMode !== 'off' 
                            ? 'text-accent-green bg-accent-green/20' 
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                        } transition-colors`}
                      >
                        {repeatMode === 'one' ? (
                          <MdRepeatOne className="h-5 w-5" />
                        ) : (
                          <MdRepeat className="h-5 w-5" />
                        )}
                      </button>
                      
                      {/* Share Button */}
                      <button
                        onClick={async () => {
                          try {
                            const trackUrl = `${window.location.origin}/music/${track.id}`;
                            
                            if (navigator.share) {
                              await navigator.share({
                                title: track.title,
                                text: `Check out this track: ${track.title} by ${track.artist || 'Unknown Artist'}`,
                                url: trackUrl
                              });
                            } else {
                              // Fallback: copy to clipboard
                              await navigator.clipboard.writeText(trackUrl);
                              // Show a toast or alert that the link was copied
                              alert('Link copied to clipboard!');
                            }
                          } catch (error) {
                            console.error('Error sharing:', error);
                            // Fallback: copy to clipboard
                            try {
                              const trackUrl = `${window.location.origin}/music/${track.id}`;
                              await navigator.clipboard.writeText(trackUrl);
                              alert('Link copied to clipboard!');
                            } catch (copyError) {
                              console.error('Error copying to clipboard:', copyError);
                              // Last fallback: show the URL
                              const trackUrl = `${window.location.origin}/music/${track.id}`;
                              prompt('Copy this link:', trackUrl);
                            }
                          }
                        }}
                        className="p-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Play/Pause Button - Right side and smaller on desktop */}
                  <button 
                    onClick={togglePlay}
                    className="w-16 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-accent-green flex items-center justify-center hover:bg-accent-green/80 transition-colors"
                  >
                    {isPlaying ? (
                      <HiPause className="h-7 w-7 sm:h-7 sm:w-7 md:h-10 md:w-10 text-black" />
                    ) : (
                      <HiPlay className="h-7 w-7 sm:h-7 sm:w-7 md:h-10 md:w-10 text-black" />
                    )}
                  </button>
                </div>
              </div>

              {/* External Links - Desktop (left-aligned) */}
              <div className="flex flex-wrap justify-start gap-3 pt-8">
                {track.spotify_url && (
                  <a 
                    href={track.spotify_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <FaSpotify className="h-5 w-5 text-green-500" />
                    <span className="text-sm hidden sm:inline">Spotify</span>
                  </a>
                )}
                {track.youtube_url && (
                  <a 
                    href={track.youtube_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <FaYoutube className="h-5 w-5 text-red-500" />
                    <span className="text-sm hidden sm:inline">YouTube</span>
                  </a>
                )}
                <button
                  onClick={openCredits}
                  className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
                >
                  <HiInformationCircle className="h-5 w-5 text-gray-400" />
                  <span className="text-sm hidden sm:inline">Credits</span>
                </button>
              </div>
            </div>
          </div>
          
          {/* Mobile Controls Section - Separate section for mobile */}
          <div className="lg:hidden pb-8">
            {/* Progress Bar */}
            <div className="space-y-2 mb-6">
              <div className="flex justify-between text-sm text-gray-400">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-accent-green to-cyan-400 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleProgressChange}
                  className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
            
            {/* Controls Row - Play button centered on mobile */}
            <div className="flex items-center justify-between">
              {/* Additional Controls - Left side */}
              <div className="flex items-center space-x-4">
                {/* Volume Control */}
                <div className="flex items-center space-x-2 w-24">
                  <button 
                    onClick={toggleMute}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {isMuted || volume === 0 ? (
                      <HiVolumeOff className="h-5 w-5" />
                    ) : (
                      <HiVolumeUp className="h-5 w-5" />
                    )}
                  </button>
                  <div className="relative h-1 bg-gray-700 rounded-full overflow-hidden flex-1">
                    <div 
                      className="absolute top-0 left-0 h-full bg-accent-green rounded-full"
                      style={{ width: `${isMuted ? 0 : volume * 100}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center">
                    <button 
                      onClick={() => toggleLike(track.id)}
                      className={`p-1.5 rounded-full ${
                        likedTracks.has(track.id) 
                          ? 'text-red-500 bg-red-500/20' 
                          : 'text-gray-3300 hover:text-red-500 hover:bg-red-500/20'
                      } transition-colors`}
                    >
                      {likedTracks.has(track.id) ? (
                        <HiHeart className="h-5 w-5" />
                      ) : (
                        <HiOutlineHeart className="h-5 w-5" />
                      )}
                    </button>
                    {track.likes > 0 && (
                      <span className="text-gray-400 text-sm ml-1">{track.likes}</span>
                    )}
                  </div>
                  
                  {/* Repeat Button */}
                  <button
                    onClick={() => {
                      if (repeatMode === 'off') {
                        setRepeatMode('one');
                      } else {
                        setRepeatMode('off');
                      }
                    }}
                    className={`p-1.5 rounded-full ${
                      repeatMode !== 'off' 
                        ? 'text-accent-green bg-accent-green/20' 
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    } transition-colors`}
                  >
                    {repeatMode === 'one' ? (
                      <MdRepeatOne className="h-5 w-5" />
                    ) : (
                      <MdRepeat className="h-5 w-5" />
                    )}
                  </button>
                  
                  {/* Share Button */}
                  <button
                    onClick={async () => {
                      try {
                        const trackUrl = `${window.location.origin}/music/${track.id}`;
                        
                        if (navigator.share) {
                          await navigator.share({
                            title: track.title,
                            text: `Check out this track: ${track.title} by ${track.artist || 'Unknown Artist'}`,
                            url: trackUrl
                          });
                        } else {
                          // Fallback: copy to clipboard
                          await navigator.clipboard.writeText(trackUrl);
                          // Show a toast or alert that the link was copied
                          alert('Link copied to clipboard!');
                        }
                      } catch (error) {
                        console.error('Error sharing:', error);
                        // Fallback: copy to clipboard
                        try {
                          const trackUrl = `${window.location.origin}/music/${track.id}`;
                          await navigator.clipboard.writeText(trackUrl);
                          alert('Link copied to clipboard!');
                        } catch (copyError) {
                          console.error('Error copying to clipboard:', copyError);
                          // Last fallback: show the URL
                          const trackUrl = `${window.location.origin}/music/${track.id}`;
                          prompt('Copy this link:', trackUrl);
                        }
                      }
                    }}
                    className="p-1.5 rounded-full text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Play/Pause Button - Centered on mobile */}
              <button 
                onClick={togglePlay}
                className="w-14 h-14 rounded-full bg-accent-green flex items-center justify-center hover:bg-accent-green/80 transition-colors"
              >
                {isPlaying ? (
                  <HiPause className="h-6 w-6 text-black" />
                ) : (
                  <HiPlay className="h-6 w-6 text-black" />
                )}
              </button>
            </div>
            
            {/* External Links */}
            <div className="flex flex-wrap justify-start gap-3 pt-8">
              {track.spotify_url && (
                <a 
                  href={track.spotify_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
                >
                  <FaSpotify className="h-5 w-5 text-green-500" />
                  <span className="text-sm hidden sm:inline">Spotify</span>
                </a>
              )}
              {track.youtube_url && (
                <a 
                  href={track.youtube_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
                >
                  <FaYoutube className="h-5 w-5 text-red-500" />
                  <span className="text-sm hidden sm:inline">YouTube</span>
                </a>
              )}
              <button
                onClick={openCredits}
                className="flex items-center space-x-2 bg-black/30 hover:bg-black/50 px-3 py-1.5 rounded-full transition-colors"
              >
                <HiInformationCircle className="h-5 w-5 text-gray-400" />
                <span className="text-sm hidden sm:inline">Credits</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Credits Modal */}
        {showCredits && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-lg z-50 flex items-center justify-center p-2 sm:p-4"
            onClick={closeCredits}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-secondary/90 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 max-w-xs sm:max-w-md w-full max-h-[70vh] overflow-y-auto border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-2 sm:mb-3">
                <h3 className="text-base sm:text-lg md:text-xl font-space-grotesk font-bold">Track Credits</h3>
                <button
                  onClick={closeCredits}
                  className="text-gray-400 hover:text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {track.roles && track.roles.length > 0 ? (
                <div className="space-y-2">
                  {track.roles
                    .filter(role => role.role && role.name) // Filter out empty roles
                    .map((role, index) => (
                      <div key={index} className="flex justify-between items-center py-1.5 border-b border-white/10">
                        <span className="font-medium text-xs sm:text-sm">{role.role}</span>
                        <span className="text-gray-300 text-xs sm:text-sm">{role.name}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-3 text-xs sm:text-sm">No credits available for this track</p>
              )}
              
              <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-white/10">
                <div className="flex justify-between text-xs sm:text-sm text-gray-400">
                  <span>Track</span>
                  <span className="truncate max-w-[60%]">{track.title}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm text-gray-400 mt-1">
                  <span>Artist</span>
                  <span className="truncate max-w-[60%]">{track.artist || 'Unknown Artist'}</span>
                </div>
                {track.album && (
                  <div className="flex justify-between text-xs sm:text-sm text-gray-400 mt-1">
                    <span>Album</span>
                    <span className="truncate max-w-[60%]">{track.album}</span>
                  </div>
                )}
                
                {/* Verified Badges */}
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-xs text-gray-400 mb-2">Verified By:</p>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-2">
                        <FaSpotify className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs text-gray-300">Spotify</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded-full overflow-hidden mr-2">
                        <img 
                          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7vQbpYxdR5FeuXKSepkKwaW8QtK_cuC-Pig&s" 
                          alt="Genius" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-xs text-gray-300">Genius</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default SingleTrack;